import css from './TimePicker2.less'
import cn from 'classnames'
import React, {useCallback, useMemo, useState} from 'react'
import BodyEnd from './BodyEnd'
import {range, times} from 'lodash'
import {useArray, useTap} from "../hooks"
import {useTestId} from '../contexts/testid'
import { usePopper } from 'react-popper';
import {BaseLink} from './external-links'
import {parseTime} from '../util/parse-time'
import {useClickOutsideHandler} from '../hooks/useClickOutsideHandler'

interface Props {
    id?: string
    className?: string
    militaryTime: boolean
    value: string
    onChange: (ev: { value: string }) => void
    disabled?: boolean
    /** @deprecated */
    fullscreen?: boolean
    testId?: string
}

function parseNum(s: string): number | null {
    const n = Number(s)
    return Number.isNaN(n) ? null : n
}


enum OpenState {
    CLOSED,
    DROPDOWN,
    FULLSCREEN,
}

// FIXME: clicking on the (mobile) backdrop clicks "through" it; sounds a bit like this ticket https://github.com/JedWatson/react-select/issues/532

export default function TimePicker2({id, disabled, className, militaryTime, value, onChange, testId, fullscreen}: Props) {
    const [openState, setOpen] = useState<OpenState>(OpenState.CLOSED)
    const [hourPicked, setHourPicked] = useState(false)
    const [minPicked, setMinPicked] = useState(false)
    const [containerRef,setContainerRef] = useState<HTMLElement|null>(null)
    const [popupRef,setPopupRef] = useState<HTMLElement|null>(null)
    const popper = usePopper(containerRef, popupRef, {placement: 'bottom-start',modifiers:[
            {
                name: 'offset',
                options: {
                    offset: [0,8],
                }
            },
        ]})
    // styles cannot be used to apply padding between the popper and its reference element or boundary. To replicate margin, use the `offset` modifier, as well as the `padding` option in the `preventOverflow` and `flip` modifiers.
    const fullTestId = useTestId(testId)

    // const fullscreen = useMemo(() => fullscreenOpt ?? window.innerWidth < 750, [fullscreenOpt]); // TODO: use some kind of window resize effect?

    // const clickHandler = useCallback((ev: React.MouseEvent<HTMLInputElement>) => {
    //     if(!isOpen) {
    //         setOpen(true);
    //         setHourPicked(false);
    //         setMinPicked(false);
    //     }
    // }, [isOpen,setOpen,setHourPicked,setMinPicked])

    const onClick = useCallback(() => {
        // console.log("CLOSE CLICK")
        setOpen(OpenState.CLOSED)
    }, [setOpen])

    const refs = useArray([containerRef, popupRef])
    const skip = useClickOutsideHandler(onClick, refs)

    const [hour, min] = useMemo(() => parseTime(value), [value])

    const clickHour = (h: number) => (ev: React.MouseEvent<HTMLElement>) => {
        ev.preventDefault()
        onChange && onChange({value: internalTime(h, min)})
        setHourPicked(true)
        if(minPicked) setOpen(OpenState.CLOSED)
    }

    const clickMin = (m: number) => (ev: React.MouseEvent<HTMLElement>) => {
        ev.preventDefault()
        setMinPicked(true)
        onChange && onChange({value: internalTime(hour, m)})
        if(hourPicked) setOpen(OpenState.CLOSED)
    }

    let popup = null

    if(openState !== OpenState.CLOSED) {
        const table = (
            <div className={css.wrap}>
                <table className={cn(css.timeTable, css.hourTable, css.borderRight)}>
                    <thead>
                        <tr>
                            {!militaryTime && <th/>}
                            <th colSpan={6}>Hour</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            {!militaryTime && <th rowSpan={2}>AM</th>}
                            {range(0, 6).map(h => <HourCell testId={testId} key={h} i={h} hour={hour} militaryTime={militaryTime}
                                                            onClick={clickHour(h)}/>)}
                        </tr>
                        <tr>
                            {range(6, 12).map(h => <HourCell testId={testId} key={h} i={h} hour={hour} militaryTime={militaryTime}
                                                             onClick={clickHour(h)}/>)}
                        </tr>
                        <tr>
                            {!militaryTime && <th rowSpan={2} className={css.pm}>PM</th>}
                            {range(12, 18).map(h => <HourCell testId={testId} key={h} i={h} hour={hour} militaryTime={militaryTime}
                                                              onClick={clickHour(h)}/>)}
                        </tr>
                        <tr>
                            {range(18, 24).map(h => <HourCell testId={testId} key={h} i={h} hour={hour} militaryTime={militaryTime}
                                                              onClick={clickHour(h)}/>)}
                        </tr>
                    </tbody>
                </table>

                <table className={cn(css.timeTable, css.minuteTable)}>
                    <thead>
                        <tr>
                            <th colSpan={3}>Minute</th>
                        </tr>
                    </thead>
                    <tbody>
                        {times(4, m => (
                            <tr key={m}>
                                {times(3, n => {
                                    const mi = m * 15 + n * 5
                                    return <MinuteCell key={mi} testId={testId} i={mi} min={min} onClick={clickMin(mi)}/>
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        )

        if(openState === OpenState.FULLSCREEN) {
            popup = (
                <div ref={setPopupRef} data-testid={fullTestId && `${fullTestId}-popup`} className={css.backdrop}>{table}</div>
            )
        } else {
            popup = (
                <div ref={setPopupRef} data-testid={fullTestId && `${fullTestId}-popup`} className={css.dropdown} {...popper.attributes.popper} style={popper.styles.popper}>{table}<span className={css.arrow}/></div>
            )
        }
    }

    const tapHandler = useCallback(ev => {
        skip()
        if(openState === OpenState.CLOSED) {
            setHourPicked(false)
            setMinPicked(false)
        }
        // if(ev.pointerType === 'mouse') {
            setOpen(OpenState.DROPDOWN)
        // } else {
        //     setOpen(OpenState.FULLSCREEN)
        // }
    }, [openState, setOpen, setHourPicked, setMinPicked])
    const tapProps = useTap(tapHandler)

    // TODO: allow typing time
    return (
        <div ref={setContainerRef}>
            <input id={id} className={className} value={displayTime(hour, min, militaryTime)} readOnly {...tapProps}
                   disabled={disabled} data-testid={fullTestId && `${fullTestId}-input`}/>
            <BodyEnd>{popup}</BodyEnd>
        </div>
    )
}

function HourCell({i, hour, onClick, militaryTime,testId}: Record<string, any>) {
    return (
        <td className={cn(
            i < 12 ? css.am : css.pm,
            i === hour && css.selected
        )}><BaseLink testId={testId && `${testId}-hr-${i}`} className={css.hour} onClick={onClick}>{militaryTime ? pad(i) : i % 12 || 12}</BaseLink></td>
    )
}

function MinuteCell({i, min, onClick, testId}: Record<string, any>) {
    return (
        <td className={cn(css.minCell, i === min && css.selected)}><BaseLink testId={testId && `${testId}-min-${i}`} className={css.min} onClick={onClick}>{pad(i)}</BaseLink>
        </td>
    )
}

function internalTime(hr: number | null, min: number | null) {
    let hrStr = 'XX'
    let pmStr = 'XM'
    if(hr != null) {
        if(hr >= 12) {
            hr -= 12
            pmStr = 'PM'
        } else {
            pmStr = 'AM'
        }
        hrStr = String(hr || 12)
    }
    return `${hrStr}:${pad(min)} ${pmStr}`
}


function displayTime(hr: number | null, min: number | null, military: boolean) {
    if(hr == null && min == null) return ''
    if(military) {
        return pad(hr) + pad(min)
    }
    return internalTime(hr, min)
}

function pad(i: number | null) {
    if(i == null) return 'XX'
    return String(i).padStart(2, '0')
}
